import React, { useCallback, useState } from "react";
import { ImgSlider } from "../../ui/components/ImgSlider";
import { Modal } from "react-bootstrap";
import { AcUnit, Biotech, Medication, Pets, Psychology } from "@mui/icons-material";
import { TagListUI } from "../../common/ui/TagListUI";

/**
 * @param {{
 *    info: {
 *     imageUrl: string,
 *     secondaryImages: string,
 *     description: string,
 *     cimavetFileUrl: string,
 *     needsPrescription: boolean,
 *     needsRefrigeration: boolean,
 *     isPsicotropic: boolean,
 *     isNarcotic: boolean,
 *     isAntibiotic: boolean,
 *     aiSummary: string,
 *     tags: Array,
 *    },
 *   children?: React.ReactNode
 * }} props
 */
export const ProductDetailInfo = ({ info, children }) => {
    const [isHovered, setIsHovered] = useState(false);
    const [show, setShow] = useState(false);

    const handleClose = useCallback(() => {
        setShow(false);
    }, []);


    const handleShowMore = useCallback(() => {
        setShow(true);
    }, []);

    const AddToFavorites = React.Children.toArray(children)
        .find(child => React.isValidElement(child) && child.type === ProductDetailInfo.AddToFavorites)

    return (
        <>
            <section className="row gap-4 gap-lg-0"
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
            >
                <div className="col-12 order-lg-first row align-items-start gap-2">
                    <div className="col-12">
                        <div className="d-flex justify-content-center align-items-center position-relative">
                            {/* Etiquetas en la esquina superior izquierda de la imagen, como en las tarjetas del catalogo */}
                            {info.tags?.length > 0 && (
                                <div className="position-absolute top-0 start-0 z-1">
                                    <TagListUI data={{ tags: info.tags }} />
                                </div>
                            )}
                            <div className="position-absolute right-50 translate-middle z-1">
                                {isHovered && AddToFavorites}
                            </div>
                            <div>
                                <ImgSlider
                                    mainImgUrl={info.imageUrl}
                                    subImageUrls={info.secondaryImages}
                                />
                            </div>
                        </div>
                    </div>
                    {!!info.aiSummary && (
                        <div className="mt-3">
                            <div className="p-3 border rounded bg-light">
                                <h6 className="fw-bold mb-2">Descripción del producto</h6>
                                <div className="mb-0 text-muted" dangerouslySetInnerHTML={{ __html: info.aiSummary }} />
                                <p className="mb-0 mt-2 text-muted fst-italic" style={{ fontSize: "0.8rem" }}>
                                    * Resumen optimizado por IA. Ver detalles precisos en la ficha técnica.
                                </p>
                            </div>
                        </div>
                    )}
                    <div className="d-flex mt-2 flex-wrap gap-2">
                        {info.needsPrescription && (
                            <span className="border rounded-pill px-3 py-1 d-flex align-items-center gap-2">
                                <Pets /> Prescripción veterinaria
                            </span>
                        )}

                        {info.isNarcotic && (
                            <span className="border rounded-pill px-3 py-1 d-flex align-items-center gap-2">
                                <Medication /> Estupefaciente
                            </span>
                        )}

                        {info.isPsicotropic && (
                            <span className="border rounded-pill px-3 py-1 d-flex align-items-center gap-2">
                                <Psychology /> Psicotrópico
                            </span>
                        )}

                        {info.isAntibiotic && (
                            <span className="border rounded-pill px-3 py-1 d-flex align-items-center gap-2">
                                <Biotech /> Antibiótico
                            </span>
                        )}

                        {info.needsRefrigeration && (
                            <span className="border rounded-pill px-3 py-1 d-flex align-items-center gap-2">
                                <AcUnit /> Conservación en frío
                            </span>
                        )}
                    </div>
                    <div className="col mt-2 d-flex flex-column flex-sm-row gap-2 justify-content-start">
                        {!!info.description && (
                            <button type="button" className="btn btn-outline-primary px-3" onClick={handleShowMore}>
                                Descripción detallada
                            </button>
                        )}
                        {
                            !!info.cimavetFileUrl && (
                                <a
                                    href={info.cimavetFileUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="btn btn-primary px-3"
                                >
                                    Ficha técnica
                                </a>
                            )
                        }
                    </div>
                </div>
            </section>
            <Modal show={show} onHide={handleClose} centered size="xl">
                <Modal.Header closeButton>
                    <Modal.Title>
                        Descripción del producto
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div dangerouslySetInnerHTML={{ __html: info.description }} />
                </Modal.Body>
            </Modal>
        </>
    );
};

ProductDetailInfo.AddToFavorites = ({ children }) => <>{children}</>;
